import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axiosInstance from '../api/axiosInstance';
import { User, UsersResponse, Role, RolesResponse } from '../types/users';
import { ChevronUpIcon, ChevronDownIcon, PencilSquareIcon, TrashIcon, PlusIcon, LockClosedIcon, UserPlusIcon, UserMinusIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import CreateUserModal from '../components/modals/CreateUserModal';
import EditUserModal from '../components/modals/EditUserModal';
import ChangePasswordModal from '../components/modals/ChangePasswordModal';
import UserStatusModal from '../components/modals/UserStatusModal';
import LoadingSpinner from '../components/LoadingSpinner';
import Pagination from '../components/ui/Pagination/Pagination';
import { Button } from '../components/ui';
import Select from '../components/ui/Input/Select';
import { useAuth } from '../contexts/AuthContext';
import { ROLE_ID_ADMIN } from '../constants/roleIds';

const Users: React.FC = () => {
  const { user: currentUser, hasPermission } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedRole, setSelectedRole] = useState<number | ''>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [sortBy, setSortBy] = useState<string>('first_name');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [statusAction, setStatusAction] = useState<'activate' | 'deactivate'>('deactivate');
  const [statusLoading, setStatusLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedUserForPassword, setSelectedUserForPassword] = useState<User | null>(null);
  const [selectedUserForStatus, setSelectedUserForStatus] = useState<User | null>(null);

  const canManageUsers = useMemo(() => {
    if (!currentUser) return false;
    return currentUser.role_id === ROLE_ID_ADMIN || hasPermission('manage_users');
  }, [currentUser, hasPermission]);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString(),
        sortBy,
        order
      });

      if (debouncedSearch) {
        params.append('search', debouncedSearch);
      }

      if (selectedRole) {
        params.append('role_id', selectedRole.toString());
      }

      console.log('Solicitando usuarios con params:', params.toString());
      const response = await axiosInstance.get<UsersResponse>(`/users/list?${params.toString()}`);
      console.log('Respuesta de la API:', response.data);
      
      if (response.data.success) {
        setUsers(response.data.data || []);
        setTotalPages(response.data.meta.totalPages || 1);
      } else {
        setUsers([]);
        setTotalPages(1);
        toast.error('Error al cargar los usuarios', { position: 'top-center' });
      }
    } catch (error) {
      console.error('Error cargando usuarios:', error);
      toast.error('Error al cargar los usuarios');
      setUsers([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearch, selectedRole, sortBy, order, pageSize]);

  const handleDelete = async (userId: number) => {
    const confirmDelete = await new Promise((resolve) => {
      toast((t) => (
        <div className="flex flex-col">
          <p>¿Confirmas que deseas eliminar este usuario?</p>
          <div className="flex justify-end space-x-2 mt-2">
            <button onClick={() => { toast.dismiss(t.id); resolve(true); }} className="px-3 py-1 bg-red-500 text-white rounded">Eliminar</button>
            <button onClick={() => { toast.dismiss(t.id); resolve(false); }} className="px-3 py-1 bg-gray-300 rounded">Cancelar</button>
          </div>
        </div>
      ),
        { duration: Infinity, position: 'top-center' }
      );
    });

    if (!confirmDelete) return;

    try {
      const token = localStorage.getItem('accessToken');
      const response = await axiosInstance.delete(`/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success(response.data.message || 'Usuario eliminado correctamente', { position: 'top-center' });
        loadUsers();
      } else {
        toast.error(response.data.message || 'Error al eliminar el usuario', { position: 'top-center' });
      }
    } catch (error: any) {
      console.error('Error al eliminar usuario:', error);
      toast.error(error.response?.data?.message || 'Error al eliminar el usuario', { position: 'top-center' });
    }
  };

  const handleEdit = (user: User) => {
    setSelectedUser(user);
    setIsEditModalOpen(true);
  };

  const handleOpenPasswordModal = (user: User) => {
    setSelectedUserForPassword(user);
    setIsPasswordModalOpen(true);
  };

  const isUserActive = (user: User): boolean => {
    if (typeof user.activo === 'number') return user.activo === 1;
    if (typeof user.activo === 'boolean') return user.activo;
    if (typeof user.is_active === 'boolean') return user.is_active;
    if (typeof user.active === 'boolean') return user.active;
    if (typeof user.status === 'string') {
      const normalizedStatus = user.status.toLowerCase();
      if (['inactive', 'inactivo', 'disabled', 'deshabilitado'].includes(normalizedStatus)) {
        return false;
      }
    }
    return true;
  };

  const handleOpenStatusModal = (user: User) => {
    const userIsActive = isUserActive(user);
    setStatusAction(userIsActive ? 'deactivate' : 'activate');
    setSelectedUserForStatus(user);
    setIsStatusModalOpen(true);
  };

  const handleToggleUserStatus = async () => {
    if (!selectedUserForStatus) return;

    try {
      setStatusLoading(true);
      const endpoint =
        statusAction === 'activate'
          ? `/users/${selectedUserForStatus.id}/activate`
          : `/users/${selectedUserForStatus.id}/deactivate`;

      const response = await axiosInstance.put(endpoint);
      if (response.data?.success) {
        toast.success(response.data.message || 'Estado del usuario actualizado', { position: 'top-center' });
        setIsStatusModalOpen(false);
        setSelectedUserForStatus(null);
        await loadUsers();
        return;
      }
      toast.error(response.data?.message || 'No fue posible actualizar el estado del usuario', {
        position: 'top-center'
      });
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Error al actualizar el estado del usuario', {
        position: 'top-center'
      });
    } finally {
      setStatusLoading(false);
    }
  };

  // Resetear a página 1 cuando cambie la búsqueda
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  // Efecto para el debounce de la búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  // Cargar roles
  useEffect(() => {
    const loadRoles = async () => {
      try {
        const response = await axiosInstance.get<RolesResponse>('/users/roles');
        if (response.data.success) {
          setRoles(response.data.roles);
        }
      } catch (error) {
        console.error('Error cargando roles:', error);
        toast.error('Error al cargar los roles', { position: 'top-center' });
      }
    };

    loadRoles();
  }, []);

  // Resetear a página 1 cuando cambie el tamaño de página
  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize]);

  // Cargar usuarios
  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setOrder('asc');
    }
  };

  const SortIcon = ({ column }: { column: string }) => {
    if (sortBy !== column) return null;
    return order === 'asc' ? (
      <ChevronUpIcon className="h-4 w-4 inline-block ml-1" />
    ) : (
      <ChevronDownIcon className="h-4 w-4 inline-block ml-1" />
    );
  };

  // Convertir roles a formato SelectOption y ordenar alfabéticamente
  const roleOptions = useMemo(() => {
    const rolesList = roles
      .map(role => ({
        value: role.id,
        label: role.name
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    
    return [
      { value: '', label: 'Todos los roles' },
      ...rolesList
    ];
  }, [roles]);

  return (
    <div className="w-full h-full p-4">
      <div className="max-w-full mx-auto">
        <div className="flex flex-col gap-4 mb-6">
          <div className="flex items-center justify-between">
            <h3  className=" ">Gestión de Usuarios</h3>
            <div className="flex items-center gap-4">
              <Select
                value={selectedRole.toString()}
                onChange={(e) => setSelectedRole(e.target.value ? Number(e.target.value) : '')}
                options={roleOptions}
                className="w-[200px]"
              />
              <Button
                onClick={() => setIsCreateModalOpen(true)}
                variant="primary"
                leftIcon={PlusIcon}
              >
                Crear
              </Button>
            </div>
          </div>

          <div className="w-full overflow-hidden mb-20">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      #
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort('first_name')}
                    >
                      <div className="flex items-center gap-2">
                        <span>Nombre</span>
                        <SortIcon column="first_name" />
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort('email')}
                    >
                      <div className="flex items-center gap-2">
                        <span>Email</span>
                        <SortIcon column="email" />
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort('role_name')}
                    >
                      <div className="flex items-center gap-2">
                        <span>Rol</span>
                        <SortIcon column="role_name" />
                      </div>
                    </th>
                    {/* <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort('agente_id')}
                    >
                   <div className="flex items-center gap-2">
                        <span>ID Agente</span>
                        <SortIcon column="agente_id" />
                      </div>
                    </th> */}
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort('created_at')}
                    >
                      <div className="flex items-center gap-2">
                        <span>Fecha de Creación</span>
                        <SortIcon column="created_at" />
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                        <LoadingSpinner size="medium" />
                      </td>
                    </tr>
                  ) : users.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                        No se encontraron usuarios
                      </td>
                    </tr>
                  ) : (
                    users.map((user, index) => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {(currentPage - 1) * pageSize + index + 1}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {`${user.first_name} ${user.last_name}`}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{user.role_name}</div>
                        </td>
                       {/*  <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{user.agente_id || '-'}</div>
                        </td> */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {new Date(user.created_at).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex space-x-2">
                            {canManageUsers && (
                              <button
                                onClick={() => handleOpenStatusModal(user)}
                                className={isUserActive(user) ? 'text-amber-500 hover:text-amber-700' : 'text-green-500 hover:text-green-700'}
                                title={isUserActive(user) ? 'Desactivar usuario' : 'Activar usuario'}
                                aria-label={`${isUserActive(user) ? 'Desactivar' : 'Activar'} usuario ${user.first_name} ${user.last_name}`}
                              >
                                {isUserActive(user) ? (
                                  <UserMinusIcon className="h-6 w-6" />
                                ) : (
                                  <UserPlusIcon className="h-6 w-6" />
                                )}
                              </button>
                            )}
                            {canManageUsers && (
                              <button
                                onClick={() => handleOpenPasswordModal(user)}
                                className="text-indigo-500 hover:text-indigo-700"
                                title="Cambiar contraseña"
                                aria-label={`Cambiar contraseña de ${user.first_name} ${user.last_name}`}
                              >
                                <LockClosedIcon className="h-6 w-6" />
                              </button>
                            )}
                            <button
                              onClick={() => handleEdit(user)}
                              className="text-blue-500 hover:text-blue-700"
                              title="Editar"
                            >
                              <PencilSquareIcon className="h-6 w-6" />
                            </button>
                            <button
                              onClick={() => handleDelete(user.id)}
                              className="text-red-500 hover:text-red-700"
                              title="Eliminar"
                            >
                              <TrashIcon className="h-6 w-6" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
            searchTerm={search}
            onSearchChange={setSearch}
            isLoading={loading}
          />
        </div>
      </div>

      <CreateUserModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={loadUsers}
        roles={roles}
      />

      <EditUserModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedUser(null);
        }}
        onSuccess={loadUsers}
        user={selectedUser}
        roles={roles}
      />

      <ChangePasswordModal
        isOpen={isPasswordModalOpen}
        onClose={() => {
          setIsPasswordModalOpen(false);
          setSelectedUserForPassword(null);
        }}
        user={selectedUserForPassword}
      />

      <UserStatusModal
        isOpen={isStatusModalOpen}
        onClose={() => {
          if (statusLoading) return;
          setIsStatusModalOpen(false);
          setSelectedUserForStatus(null);
        }}
        onConfirm={handleToggleUserStatus}
        loading={statusLoading}
        userName={selectedUserForStatus ? `${selectedUserForStatus.first_name} ${selectedUserForStatus.last_name}` : ''}
        action={statusAction}
      />
    </div>
  );
};

export default Users;